import React, { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConnie } from "@/context/ConnieContext";
import { X, Send, Settings, Sparkles, LifeBuoy, Check } from "lucide-react";

// Connie's proactive hints + sound effects were removed per product decision.
// She now only opens on demand (from the Profile menu) and never chimes or nudges.

const TOUR_STEPS = [
  { icon: "👋", title: "Hi, I'm Connie!", text: "I'm your built-in guide to Salesman Connect. Ask me anything, hiring, getting hired, Core Credits, or a quick sales tip." },
  { icon: "💡", title: "Help, only when you want it", text: "Tap the little yellow “?” or open me on any page and I'll explain exactly what to do there. I won't interrupt you otherwise." },
  { icon: "🛟", title: "Stuck? I've got you", text: "For anything I can't solve, billing, credits, account questions, I'll connect you straight to our team. Let's get started!" },
];

export default function ConnieWidget() {
  const location = useLocation();
  const { user } = useAuth();
  const { hidden } = useConnie();
  const [open, setOpen] = useState(false);
  const [tour, setTour] = useState(null);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Hey there, I'm Connie! 😊 I'm your guide to Salesman Connect, ask me anything about hiring, getting hired, credits, or sales tips!" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escStep, setEscStep] = useState(null); // null | 'form' | 'sent'
  const [escEmail, setEscEmail] = useState(user?.email || "");
  const [escSending, setEscSending] = useState(false);
  const [pendingPost, setPendingPost] = useState(null);
  const [published, setPublished] = useState(null);
  const isOwner = user?.role === "owner";
  const sessionId = useRef(`connie_${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, open]);

  const openChat = () => {
    setOpen(true);
    try { if (!localStorage.getItem("connie_tour_done")) setTour(0); } catch { /* storage blocked */ }
  };

  useEffect(() => {
    const handler = () => { if (open) { setOpen(false); } else { openChat(); } };
    window.addEventListener("connie-open", handler);
    return () => window.removeEventListener("connie-open", handler);
  });

  const finishTour = () => { try { localStorage.setItem("connie_tour_done", "1"); } catch { /* storage blocked */ } setTour(null); };

  if (hidden || location.pathname.startsWith("/messages")) return null;

  const send = async (preset) => {
    const text = (preset || input).trim();
    if (!text || busy) return;
    setInput("");
    setEscStep(null);
    const base = Date.now();
    setMessages((m) => [...m, { id: `u-${base}`, role: "user", content: text }, { id: `a-${base}`, role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current, history: messages.slice(-10).map((mm) => ({ role: mm.role, content: mm.content })).filter((mm) => mm.content) }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const shown = acc.split("[[CREATE_POST]]")[0].split("[[SET_ZODIAC]]")[0].trim();
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: shown || "…" }; return c; });
      }
      const zcmd = acc.match(/\[\[SET_ZODIAC\]\]\s*(\{[\s\S]*?\})/);
      if (zcmd) {
        try {
          const parsed = JSON.parse(zcmd[1]);
          if (parsed.sign) {
            await api.post("/magicball/zodiac", { sign: parsed.sign });
            window.dispatchEvent(new CustomEvent("connie-zodiac-changed", { detail: { sign: parsed.sign } }));
          }
        } catch (err) { console.error("bad SET_ZODIAC json", err); }
      }
      if (isOwner) {
        const cmd = acc.match(/\[\[CREATE_POST\]\]\s*(\{[\s\S]*\})/);
        if (cmd) { try { setPendingPost(JSON.parse(cmd[1])); } catch (err) { console.error("bad CREATE_POST json", err); } }
      }
    } catch (e) {
      console.error("Connie chat failed", e);
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: "Oops, I hit a snag! Mind trying that again?" }; return c; });
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    setEscSending(true);
    const concern = messages.filter((m) => m.role === "user").map((m) => m.content).join("\n") || input;
    try {
      await fetch(`${API}/connie/escalate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: concern, email: escEmail.trim(), session_id: sessionId.current, user_id: user?.user_id || "", name: user?.name || "", role: user?.role || "" }),
      });
      setEscStep("sent");
      setMessages((m) => [...m, { id: `esc-${Date.now()}`, role: "assistant", content: "Done, I've opened a support conversation with our team (Alex, our founder, will reply). You can continue the chat anytime in your Messages inbox. 💜" }]);
    } catch (e) {
      console.error("Connie escalation failed", e);
      setMessages((m) => [...m, { id: `esc-${Date.now()}`, role: "assistant", content: "Hmm, I couldn't reach the team just now. Please try again in a moment." }]);
    } finally { setEscSending(false); }
  };

  const publishPost = async () => {
    const payload = pendingPost; setPendingPost(null);
    setMessages((m) => [...m, { id: `pub-${Date.now()}`, role: "assistant", content: "On it, publishing to The Connie Connection now, give me a few seconds ✍️" }]);
    try {
      const r = await api.post("/connie/owner/create-post", payload);
      setPublished({ slug: r.data.slug, title: r.data.title });
      setMessages((m) => [...m, { id: `pubd-${Date.now()}`, role: "assistant", content: `Done! “${r.data.title}” is live on The Connie Connection. 🎉` }]);
    } catch (e) {
      console.error("publish failed", e);
      setMessages((m) => [...m, { id: `pube-${Date.now()}`, role: "assistant", content: "Hmm, publishing hit a snag. Mind trying again?" }]);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const offerDetected = !!lastAssistant && (/send support a message|forward this to a live person|send (this|it) to (our|the) support team/i.test(lastAssistant.content) || lastAssistant.content.includes("alex@salesmanconnect.com")) && !busy && escStep !== "sent";

  return (
    <>
      {open && (
        <div className="fixed bottom-28 lg:bottom-24 right-4 lg:right-6 z-50 w-[92vw] max-w-sm h-[30rem] glass rounded-2xl flex flex-col overflow-hidden fade-up" data-testid="connie-panel">
          <div className="px-4 py-3 border-b border-white/10 bg-violet-600/20 flex items-center gap-3">
            <img src="/connie-avatar.png" alt="Connie" className="w-10 h-10 rounded-full object-cover bg-violet-600/30" />
            <div className="flex-1">
              <p className="font-bold text-white text-sm flex items-center gap-1">Connie <Sparkles className="w-3 h-3 text-emerald-400" /></p>
              <p className="text-xs text-slate-400">Your Salesman Connect guide</p>
            </div>
            <Link to="/settings" data-testid="connie-settings-link" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><Settings className="w-4 h-4" /></Link>
            <button data-testid="connie-close-panel" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {tour !== null && (
            <div className="absolute inset-0 z-10 bg-[#0F172A]/95 backdrop-blur-sm flex flex-col p-6 fade-up" data-testid="connie-tour">
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="text-4xl mb-3">{TOUR_STEPS[tour].icon}</div>
                <h3 className="text-lg font-bold text-white" data-testid="connie-tour-title">{TOUR_STEPS[tour].title}</h3>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed max-w-[16rem]">{TOUR_STEPS[tour].text}</p>
                <div className="flex items-center gap-1.5 mt-5">
                  {TOUR_STEPS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === tour ? "w-5 bg-violet-400" : "w-1.5 bg-white/20"}`} />)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button data-testid="connie-tour-skip" onClick={finishTour} className="text-xs text-slate-500 hover:text-slate-300">Skip</button>
                <div className="flex gap-2">
                  {tour > 0 && <button data-testid="connie-tour-back" onClick={() => setTour((t) => t - 1)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-white/25 transition-colors">Back</button>}
                  <button data-testid="connie-tour-next" onClick={() => (tour < TOUR_STEPS.length - 1 ? setTour((t) => t + 1) : finishTour())}
                    className="text-xs font-bold px-4 py-1.5 rounded-full bg-violet-600 hover:bg-fuchsia-600 text-white transition-colors">
                    {tour < TOUR_STEPS.length - 1 ? "Next" : "Start chatting"}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-violet-600 text-white" : "bg-white/5 text-slate-200 border border-white/10"}`}>{m.content || "…"}</div>
              </div>
            ))}

            {pendingPost && (
              <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-3 space-y-2" data-testid="connie-publish-box">
                <p className="text-xs text-slate-200">Ready to publish to The Connie Connection:</p>
                <p className="text-sm font-semibold text-white">{pendingPost.subject}</p>
                <div className="flex gap-2">
                  <button data-testid="connie-publish-post" onClick={publishPost}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white transition-colors">
                    <Sparkles className="w-3.5 h-3.5" /> Publish this post
                  </button>
                  <button data-testid="connie-publish-cancel" onClick={() => setPendingPost(null)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-white/25 transition-colors">
                    Not yet
                  </button>
                </div>
              </div>
            )}
            {published && (
              <div className="pt-1" data-testid="connie-published">
                <Link to={`/connie-connection/${published.slug}`} onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300">
                  <Check className="w-3.5 h-3.5" /> View “{published.title}”
                </Link>
              </div>
            )}

            {offerDetected && escStep !== "form" && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="connie-escalate-offer">
                <button data-testid="connie-escalate-yes" onClick={() => setEscStep("form")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 transition-colors">
                  <LifeBuoy className="w-3.5 h-3.5" /> Send support a message
                </button>
                <button data-testid="connie-escalate-no" onClick={() => setEscStep("sent")}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-white/25 transition-colors">
                  No thanks
                </button>
              </div>
            )}

            {offerDetected && escStep === "form" && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="connie-escalate-form">
                <p className="text-xs text-slate-300">Drop your email so the team can reach you, then I'll send it right over.</p>
                <input data-testid="connie-escalate-email" type="email" value={escEmail} onChange={(e) => setEscEmail(e.target.value)}
                  placeholder="you@email.com" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-violet-500" />
                <button data-testid="connie-escalate-send" onClick={escalate} disabled={escSending}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-violet-600 hover:bg-fuchsia-600 text-white transition-colors disabled:opacity-50">
                  {escSending ? "Sending…" : <><Send className="w-3.5 h-3.5" /> Send to the team</>}
                </button>
              </div>
            )}

            {escStep === "sent" && lastAssistant && /opened a support conversation/i.test(lastAssistant.content) && (
              <div className="flex items-center gap-1.5 text-xs text-emerald-400 pt-1" data-testid="connie-escalate-sent"><Check className="w-3.5 h-3.5" /> Sent to support · check Messages</div>
            )}

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {["How do credits work?", "Help me set up", "Give me a sales tip"].map((q) => (
                  <button key={q} onClick={() => send(q)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-violet-500/50 transition-colors">{q}</button>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input data-testid="connie-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Connie anything…" className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white outline-none focus:border-violet-500" />
            <button data-testid="connie-send" onClick={() => send()} disabled={busy} className="w-9 h-9 rounded-full bg-violet-600 hover:bg-fuchsia-600 flex items-center justify-center transition-colors disabled:opacity-50"><Send className="w-4 h-4 text-white" /></button>
          </div>
        </div>
      )}
    </>
  );
}
